import { useEffect, useState } from 'react';
import SumsubWebSdk from '@sumsub/websdk-react';
import SplashScreen from '../../features/auth/components/SplashScreen/SplashScreen';
import { generateKycSubmission, getKycSubmission } from '../../services/kyc/kyc.service';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from '../../services/common/lib/reducerState';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { isAxiosError } from 'axios';
import { Navigate } from 'react-router-dom';
import ROUTES from '../../routers/Routes';
import { AuthEvents, authActionCreator } from '../../services/auth/authActions';
import { KycStatus } from '../../services/kyc/kyc.types';
import KycPending from './components/KycPending';
import Modal from '@/components/Modal/Modal';
import { SumsubEvent, SumsubSdkAuthError, SumsubStatusChange } from './interfaces/sumsub.types';

const KycSumsub = () => {
  const dispatch = useDispatch();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<KycStatus>(KycStatus.PENDING);
  const [kycMessage, setKycMessage] = useState<string>('');
  const userId = useSelector((state: ReducerState) => state.auth.id);
  const kycApproved = useSelector((state: ReducerState) => !!state.auth.kyc?.approved);

  const accessTokenExpirationHandler = async (userId: string, error: SumsubSdkAuthError) => {
    try {
      const { data } = await generateKycSubmission(userId);
      if (!data.kycData?.accessToken) {
        throw new Error('Access token not found');
      }
    } catch (error) {
      setKycMessage('Something went wrong. Please refresh the page.');
    }
  };

  const fetchUserSubmission = async (userId: string) => {
    try {
      const { data } = await getKycSubmission(userId);

      if (data.approved) {
        dispatch(authActionCreator(AuthEvents.FETCH_AUTHENTICATED_USER_DATA));
        setKycStatus(KycStatus.APPROVED);
        return;
      }

      if (!data.kycData) throw new Error('Access token not found');

      if (data.kycData.status === KycStatus.REJECTED) {
        setKycMessage('Your KYC has been rejected. Please contact support.');
        return;
      }

      if (data.kycData?.status === KycStatus.ON_HOLD) {
        setKycStatus(KycStatus.ON_HOLD);
        return;
      }

      setAccessToken(data.kycData.accessToken);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        const { data } = await generateKycSubmission(userId);

        if (!data.kycData) return setKycMessage('Something went wrong with the kyc provider.');

        setAccessToken(data.kycData.accessToken);
      }
    }
  };

  useEffect(() => {
    if (userId)
      (async () => {
        await fetchUserSubmission(userId);
      })();
  }, [userId]);

  const handleStatusChange = (event: SumsubEvent, status: SumsubStatusChange) => {
    if (
      event === 'idCheck.onApplicantStatusChanged' &&
      status?.reviewStatus === 'completed' &&
      status?.reviewResult?.reviewAnswer === 'GREEN'
    )
      dispatch(authActionCreator(AuthEvents.FETCH_AUTHENTICATED_USER_DATA));
  };

  if (kycApproved) return <Navigate to={ROUTES.LOBBY} />;

  if (userId && kycStatus === KycStatus.ON_HOLD)
    return (
      <SplashScreen logoHeader={true}>
        <KycPending userId={userId} />
      </SplashScreen>
    );

  return (
    <SplashScreen logoHeader={false}>
      {userId && accessToken && (
        <Modal open={true}>
          <div className="h-full">
            <SumsubWebSdk
              accessToken={accessToken}
              config={{
                theme: 'dark',
              }}
              className="scrollbar-phenom h-full max-h-[75dvh] w-full overflow-auto scrollbar-track-pn-violet-chark scrollbar-thumb-ph-primary-violet"
              expirationHandler={async (message: SumsubSdkAuthError) => {
                await accessTokenExpirationHandler(userId, message);
              }}
              onMessage={handleStatusChange}
              onError={async (message: SumsubSdkAuthError) => {
                await accessTokenExpirationHandler(userId, message);
              }}
            />
          </div>
        </Modal>
      )}

      <div className="flex h-[60dvh] items-center justify-center">
        {userId && !accessToken && !kycMessage && (
          <div className="h-40-dvh w-full">
            <LoadingSpinner size="23" />
          </div>
        )}

        {kycMessage && (
          <div className="flex flex-col items-center p-5">
            <p className="font-manrope font-bold text-ph-gray-white">{kycMessage}</p>
          </div>
        )}
      </div>
    </SplashScreen>
  );
};

export default KycSumsub;
